import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  CardBody,
  Card,
  Table,
  Badge,
  Spinner,
  Input,
  Label,
  Pagination,
  PaginationItem,
  PaginationLink,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
} from "reactstrap";
import { get, post_new } from "../../helpers/api_helper";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Link } from "react-router-dom";

const initialState = {
  id: null,
  name: "",
  email: "",
  phone: "",
  wallet_balance_ron: "0.00",
  credits: [],
};

const Users = () => {
  document.title = "Utilizatori | PagaSunt Admin";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({});
  const [filters, setFilters] = useState({
    email: "",
    phone: "",
    sort_by: "created_at",
    sort_order: "desc",
    page: 1,
  });
  const [modal, setModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(initialState);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const query = `email=${filters.email}&phone=${filters.phone}&sort_by=${filters.sort_by}&sort_order=${filters.sort_order}&page=${filters.page}`;
      const response = await get(`/admin/users?${query}`);

      if (response.status === "success") {
        setUsers(response.data.data);
        setMeta(response.data);
      }
    } catch (error) {
      console.error("Eroare la încărcarea utilizatorilor:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchUsers();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [filters]);

  const handleFilterChange = e => {
    setFilters({ ...filters, [e.target.name]: e.target.value, page: 1 });
  };

  const handlePageChange = pageNumber => {
    setFilters({ ...filters, page: pageNumber });
  };

  const toggleModal = () => {
    setModal(!modal);
    if (modal) {
      setCurrentUser(initialState);
    }
  };

  const handleEdit = async userId => {
    try {
      const response = await get(`/admin/users/${userId}`);
      if (response.status === "success") {
        const data = response.data;
        setCurrentUser({
          id: data.user.id,
          name: data.user.name || "",
          email: data.user.email || "",
          phone: data.user.phone || "",
          wallet_balance_ron: data.wallet_balance_ron || "0.00",
          credits: data.credits || [],
        });
        setModal(true);
      }
    } catch (error) {
      console.error("Eroare la încărcarea utilizatorului:", error);
      alert("Nu am putut încărca detaliile utilizatorului.");
    }
  };

  const handleCreditChange = (categoryId, value) => {
    setCurrentUser(prev => ({
      ...prev,
      credits: prev.credits.map(credit =>
        credit.category_id === categoryId
          ? { ...credit, credits: value }
          : credit
      ),
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);

    try {
      await post_new(`/admin/users/${currentUser.id}`, {
        name: currentUser.name,
        email: currentUser.email,
        phone: currentUser.phone,
        wallet_balance_ron: currentUser.wallet_balance_ron,
        credits: currentUser.credits.map(credit => ({
          category_id: credit.category_id,
          credits: Number(credit.credits || 0),
        })),
      });

      toggleModal();
      fetchUsers();
    } catch (error) {
      console.error("Eroare la salvarea utilizatorului:", error);
      alert(error?.response?.data?.message || "A apărut o eroare la salvare.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="PagaSunt" breadcrumbItem="Gestionare Utilizatori" />

          <Card>
            <CardBody>
              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Caută după Email</Label>
                    <Input
                      type="text"
                      name="email"
                      placeholder="exemplu@mail.com"
                      value={filters.email}
                      onChange={handleFilterChange}
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Caută după Telefon</Label>
                    <Input
                      type="text"
                      name="phone"
                      placeholder="0722..."
                      value={filters.phone}
                      onChange={handleFilterChange}
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Sortează după</Label>
                    <Input type="select" name="sort_by" value={filters.sort_by} onChange={handleFilterChange}>
                      <option value="created_at">Data Înscrierii</option>
                      <option value="email">Email</option>
                      <option value="name">Nume</option>
                    </Input>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  {loading ? (
                    <div className="text-center p-5"><Spinner color="primary" /></div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table-centered table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Nume / ID</th>
                            <th>Contact</th>
                            <th>Portofel</th>
                            <th>Credite</th>
                            <th>Rol</th>
                            <th>Data Înscrierii</th>
                            <th className="text-center">Acțiuni</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map(user => (
                            <tr key={user.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="avatar-xs bg-soft-primary text-primary rounded-circle text-center p-1 me-2">
                                    {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                                  </div>
                                  <div>
                                    <h5 className="font-size-14 mb-0">{user.name || "N/A"}</h5>
                                    <small className="text-muted">ID: #{user.id}</small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div><i className="uil uil-envelope-alt me-1"></i> {user.email || "-"}</div>
                                <div><i className="uil uil-phone me-1"></i> {user.phone || "-"}</div>
                              </td>
                              <td>
                                <strong>{((user.wallet?.balance || 0) / 100).toFixed(2)} {user.wallet?.currency || "RON"}</strong>
                              </td>
                              <td style={{ minWidth: 220 }}>
                                {user.category_credits?.length ? (
                                  user.category_credits.map(credit => (
                                    <Badge key={credit.id} color="light" className="me-1 text-dark mb-1">
                                      {credit.category?.name}: {credit.credits}
                                    </Badge>
                                  ))
                                ) : (
                                  <span className="text-muted">0</span>
                                )}
                              </td>
                              <td>
                                <Badge color={user.role === "admin" ? "danger" : "info"}>
                                  {user.role === "admin" ? "Admin" : "Utilizator"}
                                </Badge>
                              </td>
                              <td>{new Date(user.created_at).toLocaleDateString("ro-RO")}</td>
                              <td className="text-center">
                                <Button size="sm" color="link" className="btn btn-sm btn-soft-primary me-1" onClick={() => handleEdit(user.id)}>
                                  <i className="uil uil-edit-alt"></i>
                                </Button>
                                <Link to={`/utilizatori/${user.id}/anunturi`} className="btn btn-sm btn-soft-info">
                                  <i className="uil uil-list-ul me-1"></i> Anunțuri
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>

                      <div className="mt-4 d-flex justify-content-center">
                        <Pagination>
                          {[...Array(meta.last_page || 0)].map((_, i) => (
                            <PaginationItem active={i + 1 === meta.current_page} key={i}>
                              <PaginationLink onClick={() => handlePageChange(i + 1)}>{i + 1}</PaginationLink>
                            </PaginationItem>
                          ))}
                        </Pagination>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={modal} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>Editează utilizatorul</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Nume</Label>
                  <Input
                    type="text"
                    value={currentUser.name}
                    onChange={e => setCurrentUser({ ...currentUser, name: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={currentUser.email}
                    onChange={e => setCurrentUser({ ...currentUser, email: e.target.value })}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Telefon</Label>
                  <Input
                    type="text"
                    value={currentUser.phone}
                    onChange={e => setCurrentUser({ ...currentUser, phone: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Sold portofel (RON)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={currentUser.wallet_balance_ron}
                    onChange={e => setCurrentUser({ ...currentUser, wallet_balance_ron: e.target.value })}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <hr />

            <h5 className="font-size-15 mb-3">Credite pe categorii principale</h5>
            <Row>
              {currentUser.credits.map(credit => (
                <Col md={6} key={credit.category_id}>
                  <FormGroup>
                    <Label>{credit.category_name}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={credit.credits}
                      onChange={e => handleCreditChange(credit.category_id, e.target.value)}
                    />
                  </FormGroup>
                </Col>
              ))}
            </Row>

            <Button color="primary" type="submit" className="w-100" disabled={saving}>
              {saving ? "Se salvează..." : "Salvează modificările"}
            </Button>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default Users;
